import React from 'react'
import { FormControl, Icon } from '@mui/material'
import { Tooltip, Typography, Box } from '@mui/material'
import { withStyles } from '@mui/styles'

import { listInputStyles } from './list-input.styles'
import { BasicTable } from '../basic-table'

/**
 * @typedef {object} ListInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {any[]} [values=[]] - Array de valores disponibles (no utilizado actualmente en la implementación).
 * @property {boolean} [multiple=false] - Indica si se permiten múltiples selecciones (no utilizado actualmente).
 * @property {boolean} [readOnly=false] - Si es `true`, el componente se muestra en modo de solo lectura.
 * @property {boolean} [selectAll=false] - Opción para seleccionar todos los items (no utilizado actualmente).
 * @property {string} [title] - El título que se muestra encima de la tabla.
 * @property {string} [tooltip] - Texto del tooltip que se muestra junto al título.
 * @property {Array<object>} value - Array de objetos que se mostrarán en la tabla.
 * @property {Array<object>} subtitle - Configuración de columnas para BasicTable.
 * @property {'small' | 'medium'} [fontSize] - Tamaño de la fuente para el título.
 * @property {boolean} [required] - Si es `true`, marca el campo como requerido.
 * @property {boolean} [disabled] - Si es `true`, deshabilita el campo.
 */

/**
 * Un componente de entrada de lista que muestra datos en formato de tabla usando BasicTable.
 * 
 * Este componente renderiza un array de objetos en formato tabular, proporcionando una forma
 * consistente de mostrar listas de datos complejos. Características principales:
 * 
 * - **Visualización tabular**: Utiliza BasicTable para renderizar los datos
 * - **Título y tooltip**: Soporte para etiquetas descriptivas con ayuda contextual
 * - **Modo solo lectura**: Funciona tanto en modo edición como solo lectura
 * - **Placeholder**: Muestra '--' cuando no hay datos
 * 
 * Actualmente, el componente funciona principalmente en modo de visualización,
 * mostrando datos existentes en formato de tabla.
 * 
 * @component
 * @example
 * const MyListDisplay = () => {
 *   const items = [
 *     { id: 1, nombre: 'Item 1', cantidad: 10 },
 *     { id: 2, nombre: 'Item 2', cantidad: 20 },
 *   ];
 * 
 *   const columns = [
 *     { key: 'nombre', label: 'Nombre' },
 *     { key: 'cantidad', label: 'Cantidad', align: 'right' },
 *   ];
 * 
 *   return (
 *     <ListInput
 *       title="Lista de Items"
 *       tooltip="Estos son los items disponibles"
 *       value={items}
 *       subtitle={columns}
 *       readOnly={true}
 *     />
 *   );
 * };
 * 
 * @param {ListInputProps} props - Las props del componente ListInput.
 */
export const ListInput = React.memo(
  withStyles(listInputStyles)(
    ({ classes, values = [], multiple = false, readOnly = false, selectAll = false, ...props }) => {
      const typographyVariant = props.fontSize === 'small' ? '2' : '1'
      const value = props.value

      const getValue = () => {
        if (value.length > 0) {
          return <BasicTable config={props.subtitle} data={value} size="small" />
        } else return '--'
      }

      return (
        <FormControl
          variant="outlined"
          className={classes.root}
          required={props.required}
          disabled={props.disabled}
        >
          <Box display="flex" flexDirection="horizontal">
            {props.title && (
              <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
                {props.title}
              </Typography>
            )}
            {props.tooltip && (
              <span style={{ fontSize: 16, marginLeft: 4 }}>
                <Tooltip
                  placement="top"
                  title={
                    <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
                      {props.tooltip}
                    </Typography>
                  }
                >
                  <Icon fontSize="inherit">help_outline</Icon>
                </Tooltip>
              </span>
            )}
          </Box>
          {readOnly ? (
            <Typography variant={`body${typographyVariant}`} color="textSecondary">
              {getValue()}
            </Typography>
          ) : (
            <Typography variant={`body${typographyVariant}`} color="textSecondary">
              {getValue()}
            </Typography>
          )}
        </FormControl>
      )
    }
  )
)
